import { useEffect, useState } from "react";
import { hivesApi } from "../../services/hivesApi";
import { feedingsApi } from "../../services/feedingsApi";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";

/**
 * "Bulk Feeding" dialog for POST /hives/batch-advanced with
 * operation: "feeding". BatchAdvancedRequest dispatches to either a
 * treatment or a feeding application across many hives; the "treatment"
 * side is already covered by the dedicated Batch Treatment dialog (same
 * fields as /hives/batch-treatment), so this is the one genuinely new
 * capability batch-advanced adds: bulk-logging a feeding across hives.
 */
export function BulkFeedingDialog({ open, onClose, hiveIds, initialParams, onApplied }) {
  const { notify } = useToast();
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState({ feeding_id: "", quantity: "", date: new Date().toISOString().slice(0, 10) });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    feedingsApi.listCatalog().then((r) => setCatalog(r.feedings)).catch(() => setCatalog([]));
    setForm({
      feeding_id: initialParams?.feeding_id ? String(initialParams.feeding_id) : "",
      quantity: initialParams?.quantity != null ? String(initialParams.quantity) : "",
      date: initialParams?.date || new Date().toISOString().slice(0, 10),
    });
  }, [open, initialParams]);

  async function submit() {
    if (!form.feeding_id || !form.quantity) return notify("Choose a feeding type and quantity", "warning");
    setSubmitting(true);
    try {
      const result = await hivesApi.batchAdvanced({
        operation: "feeding",
        hive_ids: hiveIds,
        feeding_id: Number(form.feeding_id),
        quantity: Number(form.quantity),
        date: form.date,
      });
      const failed = result.errors?.length || 0;
      notify(
        failed > 0
          ? `Feeding logged for ${result.logged} of ${result.requested} hive(s); ${failed} failed`
          : `Feeding logged for ${result.logged} of ${result.requested} hive(s)`,
        failed > 0 ? "warning" : "success"
      );
      onApplied?.();
      onClose();
    } catch (err) {
      notify(err.apiMessage || "Bulk feeding failed", "danger");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bulk Feeding"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? "Applying…" : `Apply to ${hiveIds.length} hive(s)`}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          This logs a feeding for {hiveIds.length} selected hive(s).
        </p>
        <label>
          Feeding type
          <select id="bulk-feeding-dialog-feeding-id" name="feeding-id"
            value={form.feeding_id}
            onChange={(e) => setForm((f) => ({ ...f, feeding_id: e.target.value }))}
            className="touch-target"
            style={{ display: "block", width: "100%" }}
          >
            <option value="">Choose feeding…</option>
            {catalog.map((c) => <option key={c.feeding_id} value={c.feeding_id}>{c.feeding_type}</option>)}
          </select>
        </label>
        <label>
          Quantity
          <input id="bulk-feeding-dialog-quantity" name="quantity"
            type="number"
            min={0}
            step="any"
            value={form.quantity}
            onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
            className="touch-target"
            style={{ display: "block", width: "100%" }}
          />
        </label>
        <label>
          Date
          <input id="bulk-feeding-dialog-date" name="date"
            type="date"
            value={form.date}
            onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            className="touch-target"
            style={{ display: "block", width: "100%" }}
          />
        </label>
      </div>
    </Modal>
  );
}
